import {
    Component,
    Inject,
    OnInit,
    ChangeDetectionStrategy
} from '@angular/core';
import {
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle
} from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { UsersService } from '../users-service/users.service';
import { MatButton } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { GrpcStatusEvent } from '@ngx-grpc/common';
import {
    GetUserResponse,
    UserGroupDto,
    UserGroupMessage,
    UpdateUserRequest
} from '../../../protos/generated/admin.pb';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-update-user',
    imports: [
        MatDialogClose,
        MatDialogContent,
        MatDialogTitle,
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButton,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        MatCheckbox,
        TranslatePipe
    ],
    templateUrl: './update-user.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './update-user.component.scss'
})
export class UpdateUserComponent implements OnInit {
    userId!: number;
    user: GetUserResponse = new GetUserResponse();
    userGroupsList: UserGroupDto[] = [];
    selectedUserGroups: number[] = [];
    UpdateErrorMessage: string = '';
    UpdateSuccessMessage: boolean = false;
    isLoading: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: number,
        private userService: UsersService,
        private dialogRef: MatDialogRef<UpdateUserComponent>
    ) {
        this.userId = data;
    }

    ngOnInit(): void {
        this.getUser();
        this.userService.getAllUserGroups().subscribe(res => {
            this.userGroupsList = res.userGroups;
        });
    }

    // Fetch user information
    getUser() {
        this.userService.getUser(this.userId).subscribe({
            next: response => {
                this.user = response;
                this.selectedUserGroups = (response.userGroups ?? []).map(
                    (g: UserGroupMessage) => g.id
                );
                this.user.firstName = this.user.firstName ?? '';
                this.user.lastName = this.user.lastName ?? '';
                this.user.phoneNumber = this.user.phoneNumber ?? '';
            },
            error: error => console.log(error)
        });
    }

    // Update user information
    updateUserInfo() {
        this.isLoading = true;
        const updateUserRequest = new UpdateUserRequest({
            id: this.userId,
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            phoneNumber: this.user.phoneNumber,
            groupIds: this.selectedUserGroups
        });

        this.userService.updateUser(updateUserRequest).subscribe({
            next: () => {
                this.UpdateSuccessMessage = true;
                this.userService.notifyUserUpdate();
                this.closeDialog();
            },
            error: (err: GrpcStatusEvent) => {
                this.UpdateErrorMessage = err.statusMessage;
                this.isLoading = false;
            },
            complete: () => {
                this.UpdateErrorMessage = '';
                this.isLoading = false;
            }
        });
    }

    // Toggle individual user group selection
    toggleUserGroupSelection(groupId: number, checked: boolean) {
        if (checked) {
            this.selectedUserGroups.push(groupId);
        } else {
            this.selectedUserGroups = this.selectedUserGroups.filter(
                id => id !== groupId
            );
        }
    }

    // Check if all user groups are selected
    areAllUserGroupsSelected(): boolean {
        return (
            this.userGroupsList.length > 0 &&
            this.selectedUserGroups.length === this.userGroupsList.length
        );
    }

    // Check if at least one but not all user groups are selected
    isSomeUserGroupSelected(): boolean {
        return (
            this.selectedUserGroups.length > 0 &&
            this.selectedUserGroups.length < this.userGroupsList.length
        );
    }

    // Select or deselect all user groups
    toggleAllUserGroups(checked: boolean) {
        if (checked) {
            this.selectedUserGroups = this.userGroupsList.map(
                group => group.id
            );
        } else {
            this.selectedUserGroups = [];
        }
    }

    async closeDialog() {
        await new Promise(resolve => setTimeout(resolve, 2000));
        this.dialogRef.close();
    }

    closeAlert() {
        this.UpdateErrorMessage = '';
        this.UpdateSuccessMessage = false;
    }

    isUserGroupValid(): boolean {
        return this.selectedUserGroups.length > 0;
    }
}
